import { RoomClient } from '../network/roomClient';
import { ServerMessage } from '../network/protocol';
import { GameRegistry } from './GameRegistry';
import { IGame, GameHostContext } from './IGame';

/**
 * PlatformManager orchestrates room lifecycle + game plugin lifecycle.
 * Replaces the old MultiplayerManager with a game-agnostic approach.
 */
export class PlatformManager extends EventTarget {
  private roomClient: RoomClient;
  private game: IGame | null = null;
  private frameInterval: ReturnType<typeof setInterval> | null = null;

  private _mode: 'idle' | 'host' | 'viewer' = 'idle';
  private _gameId: string = '';

  get mode() { return this._mode; }
  get client() { return this.roomClient; }
  get currentGame() { return this.game; }
  get gameId() { return this._gameId; }

  setGameId(gameId: string) { this._gameId = gameId; }

  constructor() {
    super();
    this.roomClient = new RoomClient();
    this.setupClientHandlers();
  }

  private setupClientHandlers() {
    this.roomClient.onMessage((msg: ServerMessage) => {
      // Forward all messages as custom events for UI to handle
      this.dispatchEvent(new CustomEvent('server_message', { detail: msg }));

      switch (msg.type) {
        case 'joined':
          // If viewer joining mid-game, apply setup
          if (this._mode === 'viewer' && this.game) {
            if (msg.gameState === 'running' || msg.gameState === 'setup') {
              if (msg.gameSetupData && Object.keys(msg.gameSetupData).length > 0) {
                this.game.applySetup(msg.gameSetupData);
                if (msg.gameState === 'running') {
                  this.game.applyResult({ type: 'start' });
                }
              }
            }
          }
          break;

        case 'game_setup':
          if (this._mode === 'viewer' && this.game) {
            this.game.applySetup(msg.data);
          }
          break;

        case 'game_start':
          if (this._mode === 'viewer' && this.game) {
            this.game.applyResult({ type: 'start' });
          }
          break;

        case 'game_finished':
          if (this._mode === 'viewer' && this.game) {
            this.game.applyResult(msg.data);
          }
          break;

        case 'game_reset':
          if (this._mode === 'viewer' && this.game) {
            this.game.applyResult({ type: 'reset' });
          }
          break;

        case 'game_effect':
          if (this._mode === 'viewer' && this.game) {
            this.game.applyResult(msg.data);
          }
          break;

        case 'host_disconnected':
          this.stopFrameSending();
          this.dispatchEvent(new CustomEvent('host_disconnected'));
          break;
      }
    });

    // Handle binary frames for viewer
    this.roomClient.onBinary((data: ArrayBuffer) => {
      if (this._mode === 'viewer' && this.game) {
        this.game.applyFrame(data);
      }
    });

    this.roomClient.addEventListener('disconnected', () => {
      this.stopFrameSending();
      this.dispatchEvent(new CustomEvent('disconnected'));
    });
  }

  /** Host: create room and connect */
  async createAndJoinRoom(nickname: string): Promise<string> {
    const roomId = await this.roomClient.createRoom();
    await this.roomClient.connect(roomId, nickname, true);
    this._mode = 'host';
    return roomId;
  }

  /** Viewer: join existing room */
  async joinRoom(roomId: string, nickname: string): Promise<void> {
    await this.roomClient.connect(roomId, nickname, false);
    this._mode = 'viewer';
  }

  /** Initialize game plugin for the current mode */
  async initGame(gameId: string, container: HTMLElement): Promise<IGame | null> {
    this._gameId = gameId;
    const game = GameRegistry.create(gameId);
    if (!game) return null;

    this.game = game;
    await game.init(container, this._mode);

    if (this._mode === 'host') {
      this.setupHostGameEvents(game);
    }

    return game;
  }

  private setupHostGameEvents(game: IGame) {
    // When game finishes, send one final frame then stop
    game.addEventListener('game:finished', (e: Event) => {
      const detail = (e as CustomEvent).detail;
      const finalFrame = game.serializeFrame();
      if (finalFrame) {
        this.roomClient.sendBinary(finalFrame);
      }
      this.stopFrameSending();
      this.roomClient.sendGameFinished(detail);
    });

    // When game resets, notify viewers
    game.addEventListener('game:reset', () => {
      this.stopFrameSending();
      this.roomClient.sendGameReset();
    });

    // When setup changes, notify viewers
    game.addEventListener('game:setup', () => {
      const setupData = game.getSetupData();
      this.roomClient.sendGameSetup(this._gameId, setupData);
    });

    // When game starts, begin frame sending
    game.addEventListener('game:started', () => {
      this.roomClient.sendGameStart();
      this.startFrameSending();
    });
  }

  /** Create a GameHostContext for the settings panel */
  createHostContext(): GameHostContext {
    return {
      getParticipants: () => {
        return this.roomClient.users
          .map(u => u.nickname);
      },
      notifySetupChanged: () => {
        if (this.game) {
          const setupData = this.game.getSetupData();
          this.roomClient.sendGameSetup(this._gameId, setupData);
        }
      },
    };
  }

  /** Host: send skill effect to viewers */
  sendEffect(data: Record<string, unknown>) {
    if (this._mode !== 'host') return;
    this.roomClient.sendGameEffect(data);
  }

  private startFrameSending() {
    this.stopFrameSending();

    this.frameInterval = setInterval(() => {
      if (!this.game) return;

      const frame = this.game.serializeFrame();
      if (frame) {
        this.roomClient.sendBinary(frame);
      }
    }, 33); // ~30fps
  }

  private stopFrameSending() {
    if (this.frameInterval) {
      clearInterval(this.frameInterval);
      this.frameInterval = null;
    }
  }

  /** Send chat message */
  chat(message: string) {
    this.roomClient.chat(message);
  }

  /** Disconnect and cleanup */
  disconnect() {
    this.stopFrameSending();
    this.roomClient.disconnect();
    if (this.game) {
      this.game.destroy();
      this.game = null;
    }
    this._mode = 'idle';
  }
}
